import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate, formatErr } from "../../api";
import { downloadFile } from "../../download";
import { SectionTitle, StatusPill, EmptyState } from "../../components/Atoms";
import { Receipt, FileXls, HandCoins, ChartBar, Wallet, Coins, CheckCircle, XCircle, CaretDown, CaretRight, Funnel, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["All","Potential","Submitted","Approved","Payout Requested","Approved for Payout","Paid","Rejected"];
const TIME_OPTS = [
  { value: "all", label: "All time" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
];

export default function AmbassadorPayoutsPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [requesting, setRequesting] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [filters, setFilters] = useState({ project: "all", status: "All", time: "all" });

  const [loadError, setLoadError] = useState(null);
  const load = async () => {
    setLoadError(null);
    try {
      const r = await api.get("/me/earnings");
      setData(r.data);
    } catch (e) {
      setLoadError(formatErr(e));
    }
  };
  useEffect(() => { load(); }, []);

  const exportXlsx = async () => {
    setBusy(true);
    try { await downloadFile("/reports/payouts/xlsx", "my-earnings.xlsx"); toast.success("Downloaded"); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const requestForProject = async (pid) => {
    setRequesting(pid);
    try {
      const { data: r } = await api.post(`/me/payout-request/${pid}`);
      toast.success(`Requested ${fmtMoney(r.amount)} payout`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setRequesting(null); }
  };

  const requestAll = async () => {
    setRequesting("ALL");
    try {
      const { data: r } = await api.post("/me/payout-request");
      toast.success(`Requested ${fmtMoney(r.amount)} across ${r.projects} project(s)`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setRequesting(null); }
  };

  const breakdown = data?.breakdown || [];
  const projectOptions = useMemo(() => [{ id: "all", title: "All projects" }, ...breakdown.map(r => ({ id: r.project_id, title: r.project_title }))], [breakdown]);

  const filtered = useMemo(() => {
    const cutoff = filters.time === "all" ? 0 : Date.now() - parseInt(filters.time, 10) * 86400 * 1000;
    return breakdown.filter(r => {
      if (filters.project !== "all" && r.project_id !== filters.project) return false;
      if (filters.status !== "All" && r.current_status !== filters.status) return false;
      if (cutoff > 0) {
        const latest = (r.payouts || [])[0]?.created_at;
        if (latest && new Date(latest).getTime() < cutoff) return false;
      }
      return true;
    });
  }, [breakdown, filters]);

  if (loadError) {
    return (
      <div className="glass p-8 flex flex-col items-center gap-3 text-center">
        <WarningCircle size={32} weight="fill" className="text-[#EC4899]" />
        <div className="text-white font-bold">Couldn't load your earnings</div>
        <div className="text-muted text-sm">{loadError}</div>
        <button onClick={load} className="btn-primary text-sm px-4 py-2 mt-2">Retry</button>
      </div>
    );
  }

  if (!data) return <div className="skeleton h-64" />;
  const total_available = filtered.reduce((s, r) => s + (r.available_to_request || 0), 0);

  return (
    <div className="space-y-5">
      <SectionTitle title="Earnings" subtitle="Your money, project-by-project"
        action={
          <button onClick={exportXlsx} disabled={busy} data-testid="amb-export-payouts"
            className="btn-ghost flex items-center gap-2 disabled:opacity-50">
            <FileXls size={16} weight="bold" /> Export .xlsx
          </button>
        } />

      {/* Top buckets */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="Potential" value={fmtMoney(data.potential_earnings)} color="#22D3EE" icon={Coins} testid="earn-potential" />
        <Tile label="Pending" value={fmtMoney(data.pending_earnings)} color="#F59E0B" icon={HandCoins} testid="earn-pending" />
        <Tile label="Wallet Balance" value={fmtMoney(data.wallet_balance)} color="#A3E635" icon={Wallet} testid="earn-wallet" />
        <Tile label="Paid" value={fmtMoney(data.paid_earnings)} color="#10B981" icon={CheckCircle} testid="earn-paid" />
      </div>

      <div className="glass p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <div className="text-white font-bold flex items-center gap-2"><ChartBar size={16} weight="bold" /> Wallet Balance ready to request</div>
          <div className="text-3xl font-black text-[#A3E635] mt-1" data-testid="wallet-balance-value">{fmtMoney(data.wallet_balance)}</div>
          <div className="text-xs text-muted">Approved earnings not yet requested for payout. Request project-wise below.</div>
        </div>
        <button onClick={requestAll} disabled={!!requesting || data.wallet_balance <= 0}
          data-testid="request-all-payout-btn"
          className="btn-primary flex items-center justify-center gap-2 disabled:opacity-50">
          <HandCoins size={16} weight="bold" /> {requesting === "ALL" ? "Requesting…" : "Request all"}
        </button>
      </div>

      {/* Filters */}
      <div className="glass p-3 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-1.5 text-xs uppercase font-bold text-muted tracking-wider"><Funnel size={14} weight="bold" /> Filter</div>
        <select value={filters.project} onChange={e => setFilters({...filters, project:e.target.value})} className="input-base text-sm py-1.5 max-w-[200px]" data-testid="earn-filter-project">
          {projectOptions.map(o => <option key={o.id} value={o.id}>{o.title}</option>)}
        </select>
        <select value={filters.status} onChange={e => setFilters({...filters, status:e.target.value})} className="input-base text-sm py-1.5 max-w-[200px]" data-testid="earn-filter-status">
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.time} onChange={e => setFilters({...filters, time:e.target.value})} className="input-base text-sm py-1.5 max-w-[180px]" data-testid="earn-filter-time">
          {TIME_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div className="ml-auto text-xs text-muted">Showing {filtered.length} of {breakdown.length} · Available: <span className="text-[#A3E635] font-bold">{fmtMoney(total_available)}</span></div>
      </div>

      {/* Project-wise table */}
      <div className="glass p-5">
        <div className="text-white font-bold mb-3 flex items-center gap-2"><Receipt size={16} weight="bold" /> Earnings by project</div>
        {filtered.length === 0 ? <EmptyState icon={Receipt} title="No earnings to show" subtitle="Apply to projects and submit tasks to start earning." /> : (
          <div className="space-y-2">
            {filtered.map(r => {
              const isOpen = expanded[r.project_id];
              const canRequest = r.available_to_request > 0;
              return (
                <div key={r.project_id} className="rounded-2xl bg-white/3 border border-white/5" data-testid={`earn-row-${r.project_id}`}>
                  <button onClick={() => setExpanded({...expanded, [r.project_id]:!isOpen})} className="w-full text-left p-4 flex items-center gap-3" data-testid={`earn-toggle-${r.project_id}`}>
                    <div className="text-muted">{isOpen ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-white font-bold truncate">{r.project_title}</div>
                        <StatusPill status={r.current_status} />
                      </div>
                      <div className="text-[11px] text-muted truncate">{r.client_name}</div>
                    </div>
                    <div className="hidden sm:grid grid-cols-4 gap-3 text-xs text-right flex-shrink-0">
                      <Num label="Potential" val={r.potential} color="#22D3EE" />
                      <Num label="Approved" val={r.approved} color="#A3E635" />
                      <Num label="Pending" val={r.pending_payout} color="#F59E0B" />
                      <Num label="Paid" val={r.paid} color="#10B981" />
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4 pl-9 space-y-3 border-t border-white/5 pt-3">
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                        <Cell label="Potential" v={r.potential} color="#22D3EE" />
                        <Cell label="Approved" v={r.approved} color="#A3E635" />
                        <Cell label="Pending Payout" v={r.pending_payout} color="#F59E0B" />
                        <Cell label="Paid" v={r.paid} color="#10B981" />
                        <Cell label="Rejected" v={r.rejected} color="#EC4899" />
                      </div>
                      <div className="flex items-center justify-between gap-3 flex-wrap pt-2 border-t border-white/5">
                        <div className="text-sm">
                          <span className="text-muted">Available to request: </span>
                          <span className="text-[#A3E635] font-black text-lg">{fmtMoney(r.available_to_request)}</span>
                        </div>
                        <button onClick={() => requestForProject(r.project_id)} disabled={!canRequest || !!requesting}
                          data-testid={`request-project-payout-${r.project_id}`}
                          className="btn-primary text-sm flex items-center gap-2 disabled:opacity-40">
                          <HandCoins size={14} weight="bold" /> {requesting === r.project_id ? "Requesting…" : "Request payout"}
                        </button>
                      </div>
                      {(r.payouts || []).length > 0 && (
                        <div className="space-y-1.5">
                          <div className="text-[10px] uppercase font-bold text-muted tracking-wider">Payout history</div>
                          {(r.payouts || []).map(p => (
                            <div key={p.id} className="flex items-center gap-2 p-2 rounded-xl bg-white/3 text-xs" data-testid={`payout-${p.id}`}>
                              <div className="flex-1 truncate">
                                <span className="text-white font-semibold">{fmtMoney(p.amount)}</span>
                                {p.note && <span className="text-muted ml-2">· {p.note}</span>}
                              </div>
                              <span className="text-[10px] text-muted">{fmtDate(p.created_at)}</span>
                              {p.status === "Rejected" ? <XCircle size={14} className="text-[#EC4899]" weight="bold" /> :
                                p.status === "Paid" ? <CheckCircle size={14} className="text-[#10B981]" weight="fill" /> : null}
                              <StatusPill status={p.status} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const Tile = ({ label, value, color, icon: Icon, testid }) => (
  <div className="glass p-4 relative overflow-hidden" data-testid={testid}>
    <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full blur-3xl" style={{ background: `${color}25` }} />
    <div className="relative">
      <div className="flex items-center gap-1.5">
        {Icon && <Icon size={12} weight="bold" style={{ color }} />}
        <div className="text-[10px] uppercase font-bold text-muted tracking-wider">{label}</div>
      </div>
      <div className="text-2xl sm:text-3xl font-black mt-1" style={{ color }}>{value}</div>
    </div>
  </div>
);

const Num = ({ label, val, color }) => (
  <div>
    <div className="text-[9px] uppercase tracking-wider text-muted font-bold">{label}</div>
    <div className="text-sm font-bold" style={{ color }}>{fmtMoney(val)}</div>
  </div>
);

const Cell = ({ label, v, color }) => (
  <div className="bg-white/5 rounded-xl px-3 py-2">
    <div className="text-[10px] uppercase text-muted font-bold">{label}</div>
    <div className="font-bold mt-0.5" style={{ color }}>{fmtMoney(v)}</div>
  </div>
);
